import { useState } from "react";
import { toast } from "sonner";
import { LogIn, UserPlus, X, Eye, EyeOff } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { useAuth } from "../lib/AuthContext";
import api from "../lib/api";

export default function AuthModal({ open, onOpenChange }) {
  const { login, register } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  const [loginData, setLoginData] = useState({ email: "", password: "" });
  const [registerData, setRegisterData] = useState({ pseudo: "", email: "", password: "", confirmPassword: "" });

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!loginData.email || !loginData.password) {
      toast.error("Veuillez remplir tous les champs");
      return;
    }
    setLoading(true);
    try {
      await login(loginData.email, loginData.password);
      toast.success("Connexion réussie !");
      onOpenChange(false);
      setLoginData({ email: "", password: "" });
    } catch (error) {
      const message = error.response?.data?.detail || "Erreur de connexion";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!registerData.pseudo || !registerData.email || !registerData.password) {
      toast.error("Veuillez remplir tous les champs");
      return;
    }
    if (registerData.password !== registerData.confirmPassword) {
      toast.error("Les mots de passe ne correspondent pas");
      return;
    }
    if (registerData.password.length < 6) {
      toast.error("Le mot de passe doit faire au moins 6 caractères");
      return;
    }
    setLoading(true);
    try {
      await register(registerData.pseudo, registerData.email, registerData.password);
      toast.success("Inscription réussie !");
      onOpenChange(false);
      setRegisterData({ pseudo: "", email: "", password: "", confirmPassword: "" });
    } catch (error) {
      const message = error.response?.data?.detail || "Erreur lors de l'inscription";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="heading text-2xl">Connexion</DialogTitle>
        </DialogHeader>

        <Tabs defaultValue="login" className="mt-4">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="login" data-testid="login-tab">
              <LogIn className="w-4 h-4 mr-2" />
              Connexion
            </TabsTrigger>
            <TabsTrigger value="register" data-testid="register-tab">
              <UserPlus className="w-4 h-4 mr-2" />
              Inscription
            </TabsTrigger>
          </TabsList>

          <TabsContent value="login">
            <form onSubmit={handleLogin} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label htmlFor="login-email">Email</Label>
                <Input
                  id="login-email"
                  data-testid="login-email-input"
                  type="email"
                  placeholder="votre@email.com"
                  value={loginData.email}
                  onChange={(e) => setLoginData(prev => ({ ...prev, email: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="login-password">Mot de passe</Label>
                <div className="relative">
                  <Input
                    id="login-password"
                    data-testid="login-password-input"
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={loginData.password}
                    onChange={(e) => setLoginData(prev => ({ ...prev, password: e.target.value }))}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <Button 
                data-testid="login-submit-button"
                type="submit" 
                className="w-full" 
                disabled={loading}
              >
                {loading ? "Connexion..." : "Se connecter"}
              </Button>
              <button
                type="button"
                className="w-full mt-2 text-sm text-slate-600 hover:text-slate-900 underline underline-offset-4"
                onClick={async () => {
                  const email = (loginData.email || "").trim();
                  if (!email) {
                    toast.error("Entre ton email, puis clique à nouveau.");
                    return;
                  }
                  try {
                    await api.post("/auth/forgot-password", { email });
                    toast.success("Si ce compte existe, un email a été envoyé.");
                  } catch {
                    toast.success("Si ce compte existe, un email a été envoyé.");
                  }
                }}
              >
                Mot de passe oublié ?
              </button>
              
            </form>
          </TabsContent>

          <TabsContent value="register">
            <form onSubmit={handleRegister} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label htmlFor="register-pseudo">Pseudo</Label>
                <Input
                  id="register-pseudo"
                  data-testid="register-pseudo-input"
                  type="text"
                  placeholder="MonPseudo"
                  value={registerData.pseudo}
                  onChange={(e) => setRegisterData(prev => ({ ...prev, pseudo: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="register-email">Email</Label>
                <Input
                  id="register-email"
                  data-testid="register-email-input"
                  type="email"
                  placeholder="votre@email.com"
                  value={registerData.email}
                  onChange={(e) => setRegisterData(prev => ({ ...prev, email: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="register-password">Mot de passe</Label>
                <Input
                  id="register-password"
                  data-testid="register-password-input"
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••"
                  value={registerData.password}
                  onChange={(e) => setRegisterData(prev => ({ ...prev, password: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="register-confirm">Confirmer le mot de passe</Label>
                <Input
                  id="register-confirm"
                  data-testid="register-confirm-input"
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••"
                  value={registerData.confirmPassword}
                  onChange={(e) => setRegisterData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                />
              </div>
              <Button 
                data-testid="register-submit-button"
                type="submit" 
                className="w-full" 
                disabled={loading}
              >
                {loading ? "Inscription..." : "S'inscrire"}
              </Button>
            </form>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
